/* eslint-disable */
import React, { useState, useEffect } from "react";
import {
  Box,
  Avatar,
  IconButton,
  Typography,
  Chip,
  MenuItem,
  Menu,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import PaidIcon from "@mui/icons-material/Paid";
import StorefrontIcon from "@mui/icons-material/Storefront";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import ApiServices from "../../config/ApiServices";
import ApiEndpoint from "../../config/ApiEndpoint";
import Constants from "../../config/Constants";
import {
  PageHeader,
  Card,
  DataTable,
  StatusBadge,
  SearchInput,
  ActionButton,
  ConfirmDialog,
} from "../shared";
import { useTheme } from "@mui/material/styles";

const Organizations = (props) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const PAGE_LIMIT = 10;
  
  const userType =
    props?.props?.profile?.user_type || props?.props?.profile?.data?.user_type;
  const token = props?.props?.profile?.token;
  const userRole = props?.props?.profile?.role || props?.props?.profile?.data?.role;
  const isSuperAdmin = userRole === "super_admin";

  const [orgList, setOrgList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [orgType, setOrgType] = useState(""); // "paid" | "master" | "sub"
  const [page, setPage] = useState(0);
  const [totalOrgs, setTotalOrgs] = useState(0);
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ 
    open: false, 
    type: 'error', 
    title: '', 
    message: '', 
    confirmText: 'Confirm',
    action: null 
  });
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [usersDialog, setUsersDialog] = useState({ open: false, org: null, users: [] });
  const [editOrgDialog, setEditOrgDialog] = useState({ open: false, org: null });
  const [editName, setEditName] = useState("");
  const [editLogoId, setEditLogoId] = useState(null);
  const [editLogoUrl, setEditLogoUrl] = useState("");
  const [editSaving, setEditSaving] = useState(false);

  // Fetch organizations only
  const fetchOrganizations = async (search = "", pageNum = 1, type = "") => {
    if (!token) return;
    setLoading(true);

    const headers = {
      "Content-Type": "application/json",
      "x-access-token": token,
    };

    try {
      const data = await ApiServices.GetApiCall(
        ApiEndpoint.ADMIN_ORGS_ALL,
        headers
      );
      setLoading(false);
      if (data?.status) {
        let allOrgs = [];
        
        // Combine all org types
        if (data.data?.paid_orgs) {
          allOrgs = [...allOrgs, ...data.data.paid_orgs.map(org => ({ ...org, org_type: "paid" }))];
        }
        if (data.data?.master_orgs) {
          allOrgs = [...allOrgs, ...data.data.master_orgs.map(org => ({ ...org, org_type: "master" }))];
        }
        if (data.data?.sub_orgs) {
          allOrgs = [...allOrgs, ...data.data.sub_orgs.map(org => ({ ...org, org_type: "sub" }))];
        }

        // Filter by type if specified
        let filtered = allOrgs;
        if (type) {
          filtered = allOrgs.filter(org => org.org_type === type);
        }

        // Filter by search
        if (search) {
          filtered = filtered.filter(org => {
            const name = (org.organisation_name || org.name || "").toLowerCase();
            const email = (org.email || "").toLowerCase();
            return name.includes(search.toLowerCase()) || email.includes(search.toLowerCase());
          });
        }

        // Filter out cancelled/deleted organizations (status: 'cancelled')
        filtered = filtered.filter(org => {
          const status = org.status || org.connection_status || "active";
          return status !== "cancelled";
        });

        setOrgList(filtered);
        setTotalOrgs(filtered.length);
      } else {
        setOrgList([]);
        setTotalOrgs(0);
      }
    } catch {
      setLoading(false);
      toast.error("Failed to fetch clubs");
    }
  };

  const uploadImage = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !token) return null;
    const formData = new FormData();
    formData.append("file", file, file.name);
    formData.append("type", "image");
    const requestOptions = {
      method: "POST",
      headers: { "x-access-token": token },
      body: formData,
    };
    try {
      const response = await fetch(`${Constants.BASE_API_URL}user/upload`, requestOptions);
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Upload error", error);
      return null;
    }
  };

  const handleOpenEditOrg = (org) => {
    setEditOrgDialog({ open: true, org });
    setEditName(org.organisation_name || org.name || "");
    setEditLogoUrl(org.logo_url || "");
    setEditLogoId(undefined); // undefined = no change, null = clear, number = set
  };

  const handleEditLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB");
      return;
    }
    if (file.type !== "image/jpeg" && file.type !== "image/png" && file.type !== "image/jpg") {
      toast.error("Only JPG and PNG files are allowed.");
      return;
    }
    const result = await uploadImage(event);
    if (result?.status && result?.data?.id) {
      setEditLogoId(result.data.id);
      if (result.data.url) setEditLogoUrl(result.data.url);
      toast.success("Image uploaded");
    } else {
      toast.error("Failed to upload image.");
    }
  };

  const handleClearLogo = () => {
    setEditLogoId(null);
    setEditLogoUrl("");
  };

  const handleSaveOrgUpdate = async () => {
    const org = editOrgDialog.org;
    if (!org?.id || !token) return;
    const nameTrimmed = editName?.trim();
    if (!nameTrimmed) {
      toast.error("Club name is required.");
      return;
    }
    const body = { id_org: org.id, name: nameTrimmed };
    if (editLogoId !== undefined) {
      body.id_item_logo = editLogoId;
    }
    setEditSaving(true);
    try {
      const headers = {
        "Content-Type": "application/json",
        "x-access-token": token,
      };
      const data = await ApiServices.PostApiCall(
        ApiEndpoint.ADMIN_ORG_UPDATE,
        JSON.stringify(body),
        headers
      );
      setEditSaving(false);
      if (data?.status) {
        toast.success(data.message || "Club updated successfully");
        setEditOrgDialog({ open: false, org: null });
        fetchOrganizations(searchQuery, page + 1, orgType);
      } else {
        toast.error(data?.message || "Failed to update club");
      }
    } catch (error) {
      setEditSaving(false);
      toast.error("Failed to update club.");
    }
  };

  useEffect(() => {
    if (token && userType === "admin") {
      fetchOrganizations(searchQuery, page + 1, orgType);
    }
  }, [token, page, orgType, userType]);

  // Table columns
  const columns = [
    {
      field: "id",
      label: "ID",
      width: 70,
    },
    {
      field: "name",
      label: "Club",
      render: (_, row) => {
        // Get logo URL directly from API response (backend now includes logo_url)
        const logoUrl = row.logo_url || "";
        const orgName = row.organisation_name || row.name || "";
        const type = row.org_type || (row.master_org_id ? "sub" : "paid");
        
        // Check if paid org has completed payment
        const isPaidOrgComplete = type === "paid" && (
          row.payment_status === "complete" || 
          row.payment_status === "completed" ||
          row.subscription_status === "active" ||
          row.subscription_status === "paid"
        );
        
        // Check if sub-org is approved
        const isSubOrgApproved = type === "sub" && (
          row.connection_status === "approved" ||
          row.status === "approved"
        );
        
        const showTick = isPaidOrgComplete || isSubOrgApproved;
        
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Avatar
              src={logoUrl}
              sx={{
                width: 40,
                height: 40,
                bgcolor: isDark ? "rgba(99, 102, 241, 0.15)" : "rgba(25, 118, 210, 0.1)",
                color: isDark ? "#818cf8" : "#1976d2",
                fontSize: "0.875rem",
                fontWeight: 600,
              }}
            >
              {orgName[0]?.toUpperCase() || "O"}
            </Avatar>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography sx={{ fontWeight: 600, color: isDark ? "#ffffff" : "#1a202c", fontSize: "0.875rem" }}>
                {row.organisation_name || row.name || "-"}
              </Typography>
              {showTick && (
                <Tooltip 
                  title={isPaidOrgComplete ? "Payment Completed" : "Approved & Verified"}
                  arrow
                >
                  <CheckCircleIcon 
                    sx={{ 
                      color: "#22c55e", // Green color for verified/approved
                      fontSize: "18px",
                      ml: 0.5,
                    }} 
                  />
                </Tooltip>
              )}
            </Box>
            <Typography sx={{ fontSize: "0.75rem", color: isDark ? "#718096" : "#718096" }}>
              {row.email || row.user?.email || "-"}
            </Typography>
          </Box>
        </Box>
        );
      },
    },
    {
      field: "users",
      label: "Members",
      width: 200,
      mobileSameLineGroup: "meta",
      render: (_, row) => {
        // Check various possible field names for user data
        // Backend should return user info as an ARRAY since orgs can have multiple users
        let users = row.users || 
                   row.members || 
                   row.user || 
                   row.member ||
                   [];
        
        // Handle case where it might be a single object (backward compatibility)
        if (!Array.isArray(users)) {
          // If it's a single object, convert to array
          if (users && typeof users === 'object' && Object.keys(users).length > 0) {
            users = [users];
          } else {
            users = [];
          }
        }
        
        // Remove duplicates based on user ID
        const uniqueUsers = [];
        const seenIds = new Set();
        
        users.forEach(user => {
          const userId = user.id || user.user_id || user.id_user;
          const userName = user.first_name || user.name || "";
          
          // Only include if it has a name and we haven't seen this ID before
          if (userName || userId) {
            if (userId && !seenIds.has(userId)) {
              seenIds.add(userId);
              uniqueUsers.push(user);
            } else if (!userId) {
              // If no ID, check by name to avoid duplicates
              const nameExists = uniqueUsers.some(u => 
                (u.first_name || u.name) === userName && (u.email || u.user?.email) === (user.email || user.user?.email)
              );
              if (!nameExists) {
                uniqueUsers.push(user);
              }
            }
          }
        });
        
        // If no user data, show "-"
        if (uniqueUsers.length === 0) {
          return (
            <Typography sx={{ color: isDark ? "#718096" : "#718096", fontSize: "0.875rem" }}>
              -
            </Typography>
          );
        }
        
        // Display avatars in a row with "+X" indicator (always show max 2 avatars, then "+X")
        const maxVisible = 2;
        const visibleUsers = uniqueUsers.slice(0, maxVisible);
        const remainingCount = uniqueUsers.length - maxVisible;
        
        return (
          <Box 
            sx={{ 
              display: "flex", 
              alignItems: "center", 
              gap: 0.5,
              cursor: "pointer",
            }}
            onClick={(e) => {
              e.stopPropagation();
              setUsersDialog({ open: true, org: row, users: uniqueUsers });
            }}
          >
            {visibleUsers.map((user, index) => {
              const profileImage = user.profile_image || user.user?.profile_image || "";
              const firstName = user.first_name || user.name || "";
              const lastName = user.last_name || "";
              const initials = `${firstName[0] || ""}${lastName[0] || ""}`.toUpperCase() || "U";
              
              return (
                <Avatar
                  key={user.id || user.user_id || index}
                  src={profileImage}
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: isDark ? "rgba(99, 102, 241, 0.15)" : "rgba(25, 118, 210, 0.1)",
                    color: isDark ? "#818cf8" : "#1976d2",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    border: isDark ? "2px solid rgba(255, 255, 255, 0.1)" : "2px solid #ffffff",
                    marginLeft: index > 0 ? "-8px" : 0,
                    zIndex: maxVisible - index,
                    "&:hover": {
                      zIndex: 10,
                      transform: "scale(1.1)",
                      transition: "all 0.2s ease",
                    },
                  }}
                >
                  {initials}
                </Avatar>
              );
            })}
            {remainingCount > 0 && (
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: isDark ? "rgba(99, 102, 241, 0.2)" : "rgba(25, 118, 210, 0.15)",
                  color: isDark ? "#818cf8" : "#1976d2",
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  border: isDark ? "2px solid rgba(255, 255, 255, 0.1)" : "2px solid #ffffff",
                  marginLeft: "-8px",
                  cursor: "pointer",
                  "&:hover": {
                    bgcolor: isDark ? "rgba(99, 102, 241, 0.3)" : "rgba(25, 118, 210, 0.25)",
                    transform: "scale(1.1)",
                    transition: "all 0.2s ease",
                  },
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setUsersDialog({ open: true, org: row, users: uniqueUsers });
                }}
              >
                +{remainingCount}
              </Avatar>
            )}
          </Box>
        );
      },
    },
    {
      field: "type",
      label: "Type",
      mobileSameLineGroup: "meta",
      render: (_, row) => {
        const type = row.org_type || (row.master_org_id ? "sub" : "paid");
        const typeLabels = {
          paid: "Paid Club",
          master: "Club Partner",
          sub: "Club",
        };
        return (
          <Chip
            label={typeLabels[type] || type}
            size="small"
            sx={{
              backgroundColor: isDark
                ? type === "master"
                  ? "rgba(139, 92, 246, 0.15)"
                  : type === "sub"
                  ? "rgba(99, 102, 241, 0.15)"
                  : "rgba(25, 118, 210, 0.15)"
                : type === "master"
                ? "rgba(156, 39, 176, 0.1)"
                : type === "sub"
                ? "rgba(25, 118, 210, 0.1)"
                : "rgba(25, 118, 210, 0.1)",
              color: isDark
                ? type === "master"
                  ? "#a78bfa"
                  : type === "sub"
                  ? "#818cf8"
                  : "#60a5fa"
                : type === "master"
                ? "#9c27b0"
                : type === "sub"
                ? "#1976d2"
                : "#1976d2",
              fontWeight: 500,
              fontSize: "0.75rem",
            }}
          />
        );
      },
    },
    {
      field: "status",
      label: "Status",
      align: "right",
      mobileSameLineGroup: "meta",
      render: (value, row) => {
        // For sub-orgs, use user.status (active/inactive) instead of connection_status (approved/pending)
        // For other orgs, use connection_status or status
        const isSubOrg = row.org_type === "sub" || row.master_org_id;
        const status = isSubOrg 
          ? (row.user?.status || "active")  // Use user.status for sub-orgs
          : (value || row.connection_status || row.status || "active");  // Use connection_status/status for others
        return <StatusBadge status={status} />;
      },
    },
  ];

  // Handle delete organization
  const handleDelete = async (orgId, orgName) => {
    if (!isSuperAdmin) {
      toast.error("You don't have permission to delete clubs");
      return;
    }
    
    setConfirmDialog({
      open: true,
      type: 'error',
      title: 'Delete Club?',
      message: `Are you sure you want to permanently delete "${orgName}"? This action cannot be undone. The club will be permanently removed.`,
      confirmText: 'Delete',
      action: async () => {
        const headers = {
          "Content-Type": "application/json",
          "x-access-token": token,
        };
        props.setIsLoaded(true);
        try {
          // Use id_org (organization ID from organisation_details table) - recommended
          const data = await ApiServices.PostApiCall(
            ApiEndpoint.UMPHIRE_DELETE,
            JSON.stringify({ id_org: orgId }),
            headers
          );
          props.setIsLoaded(false);
          if (data?.status) {
            toast.success(data.message || "Club deleted successfully");
            fetchOrganizations(searchQuery, page + 1, orgType);
          } else {
            toast.error(data?.message || "Failed to delete club");
          }
        } catch (error) {
          props.setIsLoaded(false);
          toast.error("Failed to delete club. Please try again.");
          console.error("Delete error:", error);
        }
      },
    });
  };

  const isOrgPaidActive = (row) =>
    row?.subscription_status === "active" ||
    row?.subscription_status === "paid" ||
    row?.hasSubscription === true;

  const isManualPaid = (row) => row?.subscription_source === "admin_manual";

  const handleEnablePaid = (orgId, orgName) => {
    if (!isSuperAdmin) {
      toast.error("Only Super Admin can enable paid access");
      return;
    }
    setConfirmDialog({
      open: true,
      type: "warning",
      title: "Enable Paid Access",
      message: `Enable paid access for "${orgName}" for 1 year from now? The club will be able to post without Stripe.`,
      confirmText: "Enable",
      action: async () => {
        const headers = {
          "Content-Type": "application/json",
          "x-access-token": token,
        };
        setConfirmLoading(true);
        try {
          const data = await ApiServices.PostApiCall(
            ApiEndpoint.ADMIN_ORG_ENABLE_PAID,
            JSON.stringify({ organisation_id: orgId }),
            headers
          );
          if (data?.status) {
            const end = data?.data?.subscription_period_end;
            const endLabel = end
              ? new Date(end).toLocaleDateString()
              : "1 year from now";
            toast.success(
              data.message
                ? `${data.message} (until ${endLabel})`
                : `Paid access enabled until ${endLabel}`
            );
            fetchOrganizations(searchQuery, page + 1, orgType);
            return true;
          }
          toast.error(data?.message || "Failed to enable paid access");
          return false;
        } catch (error) {
          toast.error("Failed to enable paid access. Please try again.");
          console.error("Enable paid error:", error);
          return false;
        } finally {
          setConfirmLoading(false);
        }
      },
    });
  };

  const handleDisablePaid = (orgId, orgName) => {
    if (!isSuperAdmin) {
      toast.error("Only Super Admin can disable paid access");
      return;
    }
    setConfirmDialog({
      open: true,
      type: "error",
      title: "Disable Paid Access",
      message: `Disable paid access for "${orgName}"? Posting and related paid features will stop immediately.`,
      confirmText: "Disable",
      action: async () => {
        const headers = {
          "Content-Type": "application/json",
          "x-access-token": token,
        };
        setConfirmLoading(true);
        try {
          const data = await ApiServices.PostApiCall(
            ApiEndpoint.ADMIN_ORG_DISABLE_PAID,
            JSON.stringify({ organisation_id: orgId }),
            headers
          );
          if (data?.status) {
            toast.success(data.message || "Paid access disabled");
            fetchOrganizations(searchQuery, page + 1, orgType);
            return true;
          }
          toast.error(data?.message || "Failed to disable paid access");
          return false;
        } catch (error) {
          toast.error("Failed to disable paid access. Please try again.");
          console.error("Disable paid error:", error);
          return false;
        } finally {
          setConfirmLoading(false);
        }
      },
    });
  };

  // Render actions column (only for super admin)
  const renderActions = isSuperAdmin ? (row) => {
    // Get the organization ID (from organisation_details table)
    // This is the 'id' field in the organization object
    const orgId = row.id; // Organization ID from organisation_details
    const orgName = row.organisation_name || row.name || "this club";
    const paidActive = isOrgPaidActive(row);
    const manualPaid = isManualPaid(row);
    // Only standalone clubs (paid_orgs list) — not partners or connected clubs
    const canTogglePaid = row.org_type === "paid";
    
    if (!orgId) {
      return null; // Can't delete if no organization ID
    }

    const paidTooltip = paidActive
      ? manualPaid
        ? (
            <Box sx={{ maxWidth: 280 }}>
              <Typography sx={{ fontWeight: 600, fontSize: "0.8rem", mb: 0.5 }}>
                Paid enabled (green) — manual
              </Typography>
              <Typography sx={{ fontSize: "0.75rem" }}>
                This club has manually enabled paid access
                {row.subscription_period_end
                  ? ` until ${new Date(row.subscription_period_end).toLocaleDateString()}`
                  : ""}
                . Click to disable.
              </Typography>
            </Box>
          )
        : (
            <Box sx={{ maxWidth: 280 }}>
              <Typography sx={{ fontWeight: 600, fontSize: "0.8rem", mb: 0.5 }}>
                Paid enabled (green) — Stripe
              </Typography>
              <Typography sx={{ fontSize: "0.75rem" }}>
                This club is paid via Stripe
                {row.subscription_period_end
                  ? ` until ${new Date(row.subscription_period_end).toLocaleDateString()}`
                  : ""}
                . Manage or cancel billing in Stripe — admin disable is only for manually enabled clubs.
              </Typography>
            </Box>
          )
      : (
          <Box sx={{ maxWidth: 280 }}>
            <Typography sx={{ fontWeight: 600, fontSize: "0.8rem", mb: 0.5 }}>
              Paid disabled (golden)
            </Typography>
            <Typography sx={{ fontSize: "0.75rem" }}>
              This club does not have paid access. Click to enable paid access for 1 year without Stripe.
            </Typography>
          </Box>
        );
    
    return (
      <Box onClick={(e) => e.stopPropagation()} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        {canTogglePaid && (
          <Tooltip arrow title={paidTooltip}>
            <IconButton
              onClick={() => {
                if (paidActive) {
                  if (!manualPaid) {
                    toast.info("This club is paid via Stripe. Cancel billing in Stripe to remove access.");
                    return;
                  }
                  handleDisablePaid(orgId, orgName);
                  return;
                }
                handleEnablePaid(orgId, orgName);
              }}
              size="small"
              sx={{
                color: paidActive
                  ? (isDark ? "#34d399" : "#059669")
                  : (isDark ? "#f59e0b" : "#d97706"),
                "&:hover": {
                  backgroundColor: paidActive
                    ? (isDark ? "rgba(52, 211, 153, 0.1)" : "rgba(5, 150, 105, 0.1)")
                    : (isDark ? "rgba(245, 158, 11, 0.1)" : "rgba(217, 119, 6, 0.1)"),
                },
              }}
            >
              <PaidIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <IconButton
          onClick={() => handleOpenEditOrg(row)}
          size="small"
          sx={{
            color: isDark ? "#818cf8" : "#1976d2",
            "&:hover": {
              backgroundColor: isDark ? "rgba(99, 102, 241, 0.1)" : "rgba(25, 118, 210, 0.1)",
            },
          }}
          title="Edit name and logo"
        >
          <EditIcon fontSize="small" />
        </IconButton>
        <IconButton
          onClick={() => handleDelete(orgId, orgName)}
          size="small"
          sx={{
            color: isDark ? "#ef4444" : "#dc2626",
            "&:hover": {
              backgroundColor: isDark ? "rgba(239, 68, 68, 0.1)" : "rgba(220, 38, 38, 0.1)",
            },
          }}
          title="Delete club permanently"
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>
    );
  } : null;

  // Org type filter options
  const orgTypeOptions = [
    { value: "", label: "All Types" },
    { value: "paid", label: "Paid Clubs" },
    { value: "master", label: "Club Partners" },
    { value: "sub", label: "Clubs" },
  ];

  // Export organizations to CSV
  const handleExport = async () => {
    if (!token) return;
    
    setIsExporting(true);
    
    try {
      // Build query string from current filters
      const queryParams = new URLSearchParams();
      if (orgType) {
        queryParams.append('type', orgType);
      }
      if (searchQuery) {
        queryParams.append('search', searchQuery);
      }

      const queryString = queryParams.toString();
      const url = `${ApiEndpoint.ADMIN_ORG_EXPORT}${queryString ? '?' + queryString : ''}`;
      
      const headers = {
        "x-access-token": token,
      };

      const { blob, filename } = await ApiServices.GetBlobApiCall(url, headers);
      
      if (!blob) {
        throw new Error('Export failed - no data returned');
      }

      // Custom filename for organization export
      const dateStr = new Date().toISOString().split('T')[0];
      const customFilename = `org-data-${dateStr}.csv`;

      // Check if blob is empty (CSV with only headers)
      const blobSize = blob.size;
      
      if (blobSize < 100) {
        const text = await blob.text();
        const lines = text.split('\n').filter(line => line.trim());
        
        if (lines.length <= 1) {
          toast.warning('Export completed but no data found matching the filters.');
        }
      }

      // Create download link
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = customFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      toast.success(`Export successful! File: ${customFilename} (${(blobSize / 1024).toFixed(2)} KB)`);
    } catch (error) {
      console.error('❌ Export error:', error);
      toast.error(error.message || 'Export failed. Check console for details.');
    } finally {
      setIsExporting(false);
    }
  };

  if (userType !== "admin") {
    return (
      <Box>
        <PageHeader title="Clubs" subtitle="Access denied." />
        <Card>
          <Typography sx={{ color: isDark ? "#cbd5e1" : "#718096" }}>
            This page is for admin users only.
          </Typography>
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Clubs"
        subtitle="Manage all platform clubs"
        breadcrumbs={[{ label: "Admin" }, { label: "Clubs" }]}
      >
        {/* Search and Filter Controls */}
        <SearchInput
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setPage(0);
            fetchOrganizations(e.target.value, 1, orgType);
          }}
          onClear={() => {
            setSearchQuery("");
            setPage(0);
            fetchOrganizations("", 1, orgType);
          }}
          placeholder="Search clubs..."
          width={280}
        />

        <ActionButton
          variant="secondary"
          startIcon={<FilterListIcon />}
          onClick={(e) => setFilterAnchor(e.currentTarget)}
        >
          {orgType
            ? orgTypeOptions.find((t) => t.value === orgType)?.label
            : "All Types"}
        </ActionButton>

        <ActionButton
          variant="primary"
          startIcon={<FileDownloadIcon />}
          onClick={handleExport}
          disabled={isExporting}
        >
          {isExporting ? 'Exporting...' : 'Export to CSV'}
        </ActionButton>

        <Menu
          anchorEl={filterAnchor}
          open={Boolean(filterAnchor)}
          onClose={() => setFilterAnchor(null)}
          PaperProps={{
            sx: {
              borderRadius: "12px",
              boxShadow: isDark
                ? "0 10px 40px rgba(0,0,0,0.5)"
                : "0 10px 40px rgba(0,0,0,0.15)",
              mt: 1,
              backgroundColor: isDark ? "#1a1a1a" : "#ffffff",
              border: isDark ? "1px solid rgba(255,255,255,0.05)" : "none",
            },
          }}
        >
          {orgTypeOptions.map((option) => (
            <MenuItem
              key={option.value}
              selected={orgType === option.value}
              onClick={() => {
                setOrgType(option.value);
                setPage(0);
                setFilterAnchor(null);
                fetchOrganizations(searchQuery, 1, option.value);
              }}
              sx={{
                fontSize: "0.875rem",
                color: isDark ? "#ffffff" : "#1a202c",
                "&.Mui-selected": {
                  backgroundColor: isDark ? "rgba(99, 102, 241, 0.15)" : "rgba(25, 118, 210, 0.08)",
                  color: isDark ? "#818cf8" : "#1976d2",
                },
                "&:hover": {
                  backgroundColor: isDark ? "rgba(255, 255, 255, 0.05)" : "#f7fafc",
                },
              }}
            >
              {option.label}
            </MenuItem>
          ))}
        </Menu>
      </PageHeader>

      {/* Active Filter Chip */}
      {orgType && (
        <Box sx={{ mb: 2 }}>
          <Chip
            label={`Type: ${orgTypeOptions.find((t) => t.value === orgType)?.label}`}
            onDelete={() => {
              setOrgType("");
              setPage(0);
              fetchOrganizations(searchQuery, 1, "");
            }}
            sx={{
              backgroundColor: isDark ? "rgba(99, 102, 241, 0.15)" : "rgba(25, 118, 210, 0.1)",
              color: isDark ? "#818cf8" : "#1976d2",
              "& .MuiChip-deleteIcon": {
                color: isDark ? "#818cf8" : "#1976d2",
                "&:hover": { color: isDark ? "#a5b4fc" : "#1565c0" },
              },
            }}
          />
        </Box>
      )}

      <Card noPadding>
        <DataTable
          columns={columns}
          data={orgList}
          loading={loading}
          actions={renderActions}
          pagination
          page={page}
          rowsPerPage={PAGE_LIMIT}
          totalCount={totalOrgs}
          onPageChange={(e, newPage) => {
            setPage(newPage);
          }}
          emptyMessage="No clubs found"
          mobileTitleColumn="name"
          mobileHiddenColumns={["id"]}
        />
      </Card>

      {/* Edit Organization dialog */}
      <Dialog
        open={editOrgDialog.open}
        onClose={() => !editSaving && setEditOrgDialog({ open: false, org: null })}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: isDark ? "#1a1a1a" : "#ffffff",
            borderRadius: "16px",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 2,
            borderBottom: isDark ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid #e2e8f0",
          }}
        >
          <Typography sx={{ fontSize: "1.25rem", fontWeight: 700, color: isDark ? "#ffffff" : "#1a202c" }}>
            Edit Club
          </Typography>
          <IconButton
            onClick={() => !editSaving && setEditOrgDialog({ open: false, org: null })}
            sx={{ color: isDark ? "#cbd5e1" : "#718096" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, px: 3 }}>
          <TextField
            fullWidth
            label="Club name"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            required
            margin="normal"
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "#f7fafc",
              },
            }}
          />
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: isDark ? "#e2e8f0" : "#2d3748", mb: 1 }}>
              Logo
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Avatar
                src={editLogoUrl}
                sx={{
                  width: 64,
                  height: 64,
                  bgcolor: isDark ? "rgba(99, 102, 241, 0.15)" : "rgba(25, 118, 210, 0.1)",
                  color: isDark ? "#818cf8" : "#1976d2",
                  fontSize: "1.5rem",
                  fontWeight: 600,
                }}
              >
                {(editOrgDialog.org?.organisation_name || editOrgDialog.org?.name || editName || "O")[0]?.toUpperCase()}
              </Avatar>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Button
                  variant="outlined"
                  component="label"
                  size="small"
                  disabled={editSaving}
                  sx={{ textTransform: "none" }}
                >
                  Upload image
                  <input type="file" hidden accept="image/jpeg,image/png,image/jpg" onChange={handleEditLogoUpload} />
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleClearLogo}
                  disabled={editSaving || (!editLogoUrl && typeof editLogoId !== "number")}
                  sx={{ textTransform: "none", color: isDark ? "#f87171" : "#dc2626", borderColor: isDark ? "#f87171" : "#dc2626" }}
                >
                  Clear logo
                </Button>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: isDark ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid #e2e8f0" }}>
          <Button onClick={() => !editSaving && setEditOrgDialog({ open: false, org: null })} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveOrgUpdate}
            disabled={editSaving}
            sx={{ textTransform: "none" }}
          >
            {editSaving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        confirmText={confirmDialog.confirmText || "Confirm"}
        cancelText="Cancel"
        onConfirm={async () => {
          if (confirmLoading) return;
          let ok = true;
          if (confirmDialog.action) {
            ok = await confirmDialog.action();
          }
          // Delete actions don't return a boolean — treat undefined as success
          if (ok === false) return;
          setConfirmDialog({ open: false, type: 'error', title: '', message: '', confirmText: 'Confirm', action: null });
        }}
        onCancel={() => {
          if (confirmLoading) return;
          setConfirmDialog({ open: false, type: 'error', title: '', message: '', confirmText: 'Confirm', action: null });
        }}
        loading={confirmLoading}
      />

      {/* Users Dialog */}
      <Dialog
        open={usersDialog.open}
        onClose={() => setUsersDialog({ open: false, org: null, users: [] })}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: isDark ? "#1a1a1a" : "#ffffff",
            borderRadius: "16px",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 2,
            borderBottom: isDark ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid #e2e8f0",
          }}
        >
          <Typography sx={{ fontSize: "1.25rem", fontWeight: 700, color: isDark ? "#ffffff" : "#1a202c" }}>
            Users - {usersDialog.org ? (usersDialog.org.organisation_name || usersDialog.org.name || "") : ""}
          </Typography>
          <IconButton
            onClick={() => setUsersDialog({ open: false, org: null, users: [] })}
            sx={{ color: isDark ? "#cbd5e1" : "#718096" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 4, px: 3, maxHeight: "70vh", overflowY: "auto" }}>
          {usersDialog.users.length === 0 ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <Typography sx={{ color: isDark ? "#cbd5e1" : "#718096" }}>No users found</Typography>
            </Box>
          ) : (
            <Box sx={{ pt: 1 }}>
              {usersDialog.users.map((user, index) => {
                const profileImage = user.profile_image || user.user?.profile_image || "";
                const firstName = user.first_name || user.name || "";
                const lastName = user.last_name || "";
                const fullName = `${firstName} ${lastName}`.trim() || "Unknown User";
                const userEmail = user.email || user.user?.email || "";
                const userRole = user.role || "";
                const userStatus = user.status || user.user?.status || "";
                const userSport = user.sport?.name || user.sport_name || "";
                const initials = `${firstName[0] || ""}${lastName[0] || ""}`.toUpperCase() || "U";
                
                return (
                  <Box
                    key={user.id || user.user_id || index}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      p: 2,
                      mb: 1,
                      borderRadius: "8px",
                      backgroundColor: isDark ? "rgba(255, 255, 255, 0.02)" : "#f7fafc",
                      border: isDark ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid #e2e8f0",
                    }}
                  >
                    <Avatar
                      src={profileImage}
                      sx={{
                        width: 56,
                        height: 56,
                        bgcolor: isDark ? "rgba(99, 102, 241, 0.15)" : "rgba(25, 118, 210, 0.1)",
                        color: isDark ? "#818cf8" : "#1976d2",
                        fontSize: "1.25rem",
                        fontWeight: 600,
                      }}
                    >
                      {initials}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontWeight: 600, color: isDark ? "#ffffff" : "#1a202c", fontSize: "0.875rem" }}>
                        {fullName}
                      </Typography>
                      {userEmail && (
                        <Typography sx={{ fontSize: "0.75rem", color: isDark ? "#718096" : "#718096" }}>
                          {userEmail}
                        </Typography>
                      )}
                      <Box sx={{ display: "flex", gap: 1, mt: 0.5, flexWrap: "wrap" }}>
                        {userRole && (
                          <Chip
                            label={userRole}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: "0.65rem",
                              backgroundColor: isDark ? "rgba(99, 102, 241, 0.2)" : "rgba(25, 118, 210, 0.15)",
                              color: isDark ? "#818cf8" : "#1976d2",
                              fontWeight: 600,
                              textTransform: "capitalize",
                            }}
                          />
                        )}
                        {userSport && (
                          <Chip
                            label={userSport}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: "0.65rem",
                              backgroundColor: isDark ? "rgba(34, 197, 94, 0.2)" : "rgba(34, 197, 94, 0.15)",
                              color: isDark ? "#4ade80" : "#22c55e",
                              fontWeight: 600,
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                    {userStatus && (
                      <StatusBadge status={userStatus} />
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: isDark ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid #e2e8f0" }}>
          <Button
            onClick={() => setUsersDialog({ open: false, org: null, users: [] })}
            sx={{ textTransform: "none" }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Organizations;
